import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Card, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Switch, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { accessVerify, capitalize } from '../../../../../utils/common';
import { useDispatch, useSelector } from '../../../../../redux/store';
import FormControlLabel from '@mui/material/FormControlLabel';
import { addAccessControl, getAccessControlById } from '../../../../../redux/slices/hrRedux/UserManagementRedux/accessControlRedux';
import { assignUserRole, getUserRoles } from '../../../../../redux/slices/hrRedux/UserManagementRedux/userRoleRedux';
import { updateUserDataAfterUpdate } from '../../../../../redux/slices/posRedux/posRedux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function UserAccessControl({ userId }) {
  const dispatch = useDispatch();
  const { data: allLocations = [] } = useSelector((state) => state.location);
  const { data: userInfo, addData: userInfoUpdated } = useSelector((state) => state.accessControl);
  const { roles = [] } = useSelector((state) => state.userRoles);
  const { permissionsList = [] } = useSelector((state) => state.common);

  const [permissions, setPermissions] = useState([]);
  const [locationPermissions, setLocationPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    dispatch(getAccessControlById(userId));
    dispatch(getUserRoles({ status: 'active' }));
  }, [userId, userInfoUpdated]);

  useEffect(() => {
    dispatch(updateUserDataAfterUpdate());
  }, [userInfoUpdated]);

  useEffect(() => {
    setLocationPermissions(userInfo?.locationPermissions || []);
    setPermissions(userInfo?.permissions || []);
    setSelectedRoleId(userInfo?.userRoleId == null ? '' : String(userInfo.userRoleId));
    setSaveError('');
  }, [userInfo]);

  const roleManaged = userInfo?.userRoleId != null;
  const roleSelected = selectedRoleId !== '';
  const permissionEditorReadOnly = roleManaged || roleSelected;

  const handleLocationToggle = (locationId) => {
    setLocationPermissions((previous) => (previous.includes(locationId) ? previous.filter((item) => item !== locationId) : [...previous, locationId]));
  };

  const handlePermissionToggle = (code) => {
    if (permissionEditorReadOnly) return;
    setPermissions((previous) => (previous.includes(code) ? previous.filter((item) => item !== code) : [...previous, code]));
  };

  const groupedPermissions = (mainCategory) =>
    permissionsList
      .filter((permission) => permission.mainCategory === mainCategory)
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .reduce((groups, permission) => {
        if (!groups[permission.category]) groups[permission.category] = [];
        groups[permission.category].push(permission);
        return groups;
      }, {});

  const handleSave = async () => {
    setSaveError('');
    if (!locationPermissions.length) {
      setSaveError('At least one location must be selected.');
      return;
    }

    if (permissionEditorReadOnly) {
      if (!accessVerify('USER_ROLE_ASSIGN')) {
        setSaveError('You do not have permission to assign or update user roles.');
        return;
      }
      const previousRoleId = userInfo?.userRoleId == null ? '' : String(userInfo.userRoleId);
      if (previousRoleId !== selectedRoleId) {
        const message = selectedRoleId
          ? 'Assigning this role will replace the user’s current direct permissions. Continue?'
          : 'Removing the role will keep the current permissions as legacy permissions. Continue?';
        if (!window.confirm(message)) return;
      }

      const response = await dispatch(
        assignUserRole({
          userId,
          roleId: selectedRoleId || null,
          locationPermissions,
        })
      );
      if (response?.status) dispatch(getAccessControlById(userId));
      return;
    }

    const response = await dispatch(
      addAccessControl({
        permissions,
        locationPermissions,
        userId,
      })
    );
    if (!response?.status) setSaveError(response?.message || 'Unable to update permissions.');
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Card sx={{ py: 3, px: 3 }} style={{ width: '100%' }}>
          <Typography variant='h4' paragraph>
            User Role Assignment
          </Typography>
          {saveError && <Alert severity='error' sx={{ mb: 2 }}>{saveError}</Alert>}
          {roleManaged ? (
            <Alert severity='info' sx={{ mb: 2 }}>
              This user is managed by the {userInfo?.userRole?.name || 'assigned'} role. Application permissions are read-only here and must be changed from User Roles.
            </Alert>
          ) : (
            <Alert severity='warning' sx={{ mb: 2 }}>
              No role is assigned. The user is using legacy direct permissions. Selecting a role will replace those permissions when saved.
            </Alert>
          )}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <FormControl fullWidth disabled={!accessVerify('USER_ROLE_ASSIGN')}>
              <InputLabel id='user-role-select-label'>User Role</InputLabel>
              <Select labelId='user-role-select-label' value={selectedRoleId} label='User Role' onChange={(event) => setSelectedRoleId(event.target.value)}>
                <MenuItem value=''>No role — keep legacy permissions</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={String(role.id)}>
                    {role.name} ({role.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Typography variant='h4' paragraph>
            Location Based Access Control
          </Typography>
          <Card sx={{ py: 3, px: 3, mb: 3 }} style={{ width: '100%' }}>
            <Grid container alignItems='center'>
              {allLocations.map((location) => (
                <Grid item xs={6} lg={3} key={location.id}>
                  <Stack direction='row' alignItems='center'>
                    <FormControlLabel
                      value={location.id}
                      control={<Switch checked={locationPermissions.includes(location.id)} onChange={() => handleLocationToggle(location.id)} />}
                      label={location.name}
                      labelPlacement='end'
                    />
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Card>

          <Typography variant='h4' paragraph>
            {roleManaged ? 'Role Permission Preview' : 'Application Access Control'}
          </Typography>
          {[...new Set(permissionsList.slice().sort((a, b) => a.priority - b.priority).map((value) => value.mainCategory))].map((mainCategory) => (
            <Accordion key={mainCategory}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{capitalize(mainCategory)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(groupedPermissions(mainCategory)).map(([category, permissionCategories]) => (
                  <Accordion key={category}>
                    {category === 'superAdminAccess' && !accessVerify('SUPER_ADMIN') ? null : (
                      <>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>{capitalize(category)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Card sx={{ py: 3, px: 3, mb: 2 }} style={{ width: '100%' }}>
                            <Typography variant='subtitle1' sx={{ mb: 2 }}>
                              {capitalize(category)}
                            </Typography>
                            <Grid container alignItems='center' justifyContent='space-between'>
                              {permissionCategories.map((permission) => (
                                <Grid item xs={6} lg={2.4} key={permission.code}>
                                  <Stack direction='row' alignItems='center'>
                                    <FormControlLabel
                                      value={permission.code}
                                      control={<Switch checked={permissions.includes(permission.code)} disabled={permissionEditorReadOnly} onChange={() => handlePermissionToggle(permission.code)} />}
                                      label={permission.title}
                                      labelPlacement='end'
                                    />
                                  </Stack>
                                </Grid>
                              ))}
                            </Grid>
                          </Card>
                        </AccordionDetails>
                      </>
                    )}
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Card>
      </Grid>

      <Box position='fixed' bottom={20} right={20} zIndex={9999}>
        <LoadingButton type='submit' variant='contained' onClick={handleSave} disabled={permissionEditorReadOnly && !accessVerify('USER_ROLE_ASSIGN')}>
          Save Changes
        </LoadingButton>
      </Box>
    </Box>
  );
}
